import { Link } from "react-router-dom";
import { SEO } from "../components/common/SEO";
import { useLanguage } from "../context/LanguageProvider";
import { useSiteData } from "../context/SiteDataProvider";
import { pick, UI_STRINGS } from "../lib/i18n";
import { ArrowRight, Calendar } from "lucide-react";

function formatDate(iso, lang) {
    if (!iso) return "";
    try {
        return new Date(iso).toLocaleDateString(
            lang === "fr" ? "fr-CA" : "en-CA",
            { year: "numeric", month: "long", day: "numeric" },
        );
    } catch {
        return "";
    }
}

export default function Blog() {
    const { lang } = useLanguage();
    const { blogPosts, siteContent } = useSiteData();
    const seo = siteContent?.seo?.blog || {};

    return (
        <div data-testid="page-blog" className="bg-ink">
            <SEO
                title={pick(seo.title, lang) || "Le blog du cabinet"}
                description={pick(seo.description, lang)}
                lang={lang}
            />
            <section className="mx-auto max-w-7xl px-5 lg:px-10 py-16 md:py-20">
                <div className="max-w-3xl">
                    <div className="eyebrow mb-3">
                        {pick(UI_STRINGS.nav.blog, lang)}
                    </div>
                    <h1 className="font-serif text-4xl md:text-5xl leading-tight text-neutral-50">
                        {pick(
                            {
                                fr: "Analyses juridiques et actualités du cabinet",
                                en: "Legal analyses and firm news",
                            },
                            lang,
                        )}
                    </h1>
                </div>

                <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                    {blogPosts.map((post) => (
                        <Link
                            key={post.slug}
                            to={`/blog/${post.slug}`}
                            className="card-line flex flex-col overflow-hidden group"
                            data-testid={`blog-card-${post.slug}`}
                        >
                            <div className="aspect-[16/9] bg-ink-deep border-b border-ink-border relative overflow-hidden bg-marble">
                                <div className="absolute inset-0 flex items-center justify-center">
                                    <span className="font-serif text-3xl text-gold opacity-40">
                                        CMABS
                                    </span>
                                </div>
                            </div>
                            <div className="p-6 flex flex-col flex-1">
                                <div className="flex items-center gap-2 text-xs text-neutral-500 mb-3">
                                    <Calendar size={12} />
                                    <span>{formatDate(post.created_at, lang)}</span>
                                </div>
                                <h2 className="font-serif text-xl text-neutral-50 leading-snug mb-3 group-hover:text-gold transition-colors">
                                    {pick(post.title, lang)}
                                </h2>
                                <p className="text-sm text-neutral-400 leading-relaxed flex-1">
                                    {pick(post.excerpt, lang)}
                                </p>
                                <span
                                    className="mt-5 text-xs uppercase tracking-widest text-gold flex items-center gap-2 group-hover:gap-3 transition-all"
                                    data-testid={`blog-read-${post.slug}`}
                                >
                                    {pick(UI_STRINGS.cta.read_more, lang)}
                                    <ArrowRight size={14} />
                                </span>
                            </div>
                        </Link>
                    ))}
                </div>
            </section>
        </div>
    );
}
